import type { KeyboardEvent, PointerEvent } from "react";
import {
    useOptionalSideNavContext,
    useSideNavContext,
    useSideNavWidth,
} from "./SideNavProvider";
import { useSideNavEdgeDrag } from "./useSideNavEdgeDrag";
import {
    fittedOpenWidth,
    MAX_OPEN_WIDTH,
    MIN_OPEN_WIDTH,
    MINI_WIDTH,
} from "./sideNavWidth";
import styles from "./SideNavResizeHandle.module.scss";

const PILL_HALF_HEIGHT_PX = 24;
/** How far one arrow key press resizes the open nav. */
const ARROW_KEY_STEP_PX = 16;

const clamp = ({ value, min, max }: Record<"value" | "min" | "max", number>) =>
    Math.min(Math.max(value, min), max);

/** Keeps the pill under the pointer, inside the nav's height. */
const followPointer = (event: PointerEvent<HTMLDivElement>) => {
    const handle = event.currentTarget;
    const { top, height } = handle.getBoundingClientRect();
    const pillY = clamp({
        value: event.clientY - top,
        min: PILL_HALF_HEIGHT_PX,
        max: height - PILL_HALF_HEIGHT_PX,
    });
    handle.style.setProperty("--side-nav-pill-y", `${pillY}px`);
};

const ResizeHandle = () => {
    const { mode, setMode, toggleMode, setHidden, setWidth, navRef } =
        useSideNavContext();
    const width = useSideNavWidth();
    const { onPointerMove, ...dragHandlers } = useSideNavEdgeDrag({
        mode,
        setMode,
        toggleMode,
        setHidden,
        width,
        setWidth,
        navRef,
    });

    const narrow = () => {
        if (mode === "mini") {
            return;
        }
        if (width > MIN_OPEN_WIDTH) {
            setWidth(Math.max(width - ARROW_KEY_STEP_PX, MIN_OPEN_WIDTH));
        } else {
            setMode("mini");
        }
    };

    const widen = () => {
        if (mode === "mini") {
            setMode("open");
            return;
        }
        const fittedWidth =
            navRef.current === null
                ? MAX_OPEN_WIDTH
                : fittedOpenWidth({ nav: navRef.current, width });
        setWidth(Math.min(width + ARROW_KEY_STEP_PX, fittedWidth));
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggleMode();
        } else if (event.key === "ArrowLeft") {
            event.preventDefault();
            narrow();
        } else if (event.key === "ArrowRight") {
            event.preventDefault();
            widen();
        }
    };

    return (
        <div
            role="separator"
            tabIndex={0}
            aria-orientation="vertical"
            aria-label="Resize navigation"
            aria-valuemin={MINI_WIDTH}
            aria-valuemax={MAX_OPEN_WIDTH}
            aria-valuenow={mode === "open" ? width : MINI_WIDTH}
            className={styles.handle}
            onKeyDown={handleKeyDown}
            onPointerMove={(event) => {
                followPointer(event);
                onPointerMove(event);
            }}
            {...dragHandlers}
        >
            <span className={styles.pill} aria-hidden />
        </div>
    );
};

/**
 * The nav's right edge as a separator. Drag it to resize the open nav (up to
 * where its longest label fits, at most 400px), past 180px to snap to mini,
 * or a mini nav nearly to its left edge to hide it. A click toggles open and
 * mini; a double-click resets the open width. Enter or Space toggles; Left
 * narrows by 16px, then goes to mini; Right goes to open, then widens. Not
 * rendered while hidden or without a `SideNavProvider`.
 */
export const SideNavResizeHandle = () => {
    const context = useOptionalSideNavContext();
    if (context === null || context.isHidden) {
        return null;
    }
    return <ResizeHandle />;
};
